/**
 * CourierWorkflowModal.js - Complete Courier/Delivery Service Workflow
 * Steps: Pickup Arrival → Verify Sender → Inspect Package → Photo → Transport → Delivery → Recipient Verify → Signature → Complete
 */
import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronRight, ChevronLeft, MapPin, Clock, Camera, Pen, 
  CheckCircle, AlertTriangle, User, Phone, Package, Truck,
  Loader2, CheckSquare, Square, Navigation, Timer, Image, FileText,
  Shield, AlertOctagon, Weight, Ruler, Banknote
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const COURIER_STEPS = {
  OVERVIEW: 'overview',
  PICKUP_ARRIVAL: 'pickup_arrival',
  VERIFY_SENDER: 'verify_sender',
  INSPECT_PACKAGE: 'inspect_package',
  PHOTO_PICKUP: 'photo_pickup',
  TRANSPORT: 'transport',
  DELIVERY_ARRIVAL: 'delivery_arrival',
  VERIFY_RECIPIENT: 'verify_recipient',
  PHOTO_DELIVERY: 'photo_delivery',
  SIGNATURE: 'signature',
  COMPLETE: 'complete'
};

const CourierWorkflowModal = ({ isOpen, onClose, booking, worker, onWorkflowComplete }) => {
  const [currentStep, setCurrentStep] = useState(COURIER_STEPS.OVERVIEW);
  const [loading, setLoading] = useState(false);
  
  // Pickup state
  const [arrivedAtPickup, setArrivedAtPickup] = useState(false);
  const [pickupArrivalTime, setPickupArrivalTime] = useState(null);
  const [senderVerified, setSenderVerified] = useState(false);
  const [senderName, setSenderName] = useState('');
  
  // Package state
  const [packageCondition, setPackageCondition] = useState('good'); // good, minor_damage, damaged
  const [packageWeight, setPackageWeight] = useState('');
  const [packageDimensions, setPackageDimensions] = useState('');
  const [packageDescription, setPackageDescription] = useState('');
  const [fragileItem, setFragileItem] = useState(false);
  const [conditionNotes, setConditionNotes] = useState('');
  
  // Photo state
  const [pickupPhotos, setPickupPhotos] = useState([]);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Transport state
  const [transportStartTime, setTransportStartTime] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  
  // Delivery state
  const [arrivedAtDelivery, setArrivedAtDelivery] = useState(false);
  const [deliveryArrivalTime, setDeliveryArrivalTime] = useState(null);
  const [recipientVerified, setRecipientVerified] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('door'); // door, reception, mailroom, other
  const [deliveryNotes, setDeliveryNotes] = useState('');
  
  // Signature state
  const [signatureData, setSignatureData] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Handle photo upload
  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      const photoObj = {
        id: Date.now(),
        url: previewUrl,
        file: file,
        timestamp: new Date().toISOString(),
        type: type
      };

      if (type === 'pickup') {
        setPickupPhotos(prev => [...prev, photoObj]);
      } else {
        setDeliveryPhotos(prev => [...prev, photoObj]);
      }
      toast.success('Photo added');
    } catch (error) {
      toast.error('Failed to add photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Signature handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#D4AF37';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        setSignatureData(canvas.toDataURL());
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  // GPS arrival
  const handleArrival = (type) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          if (type === 'pickup') {
            setArrivedAtPickup(true);
            setPickupArrivalTime(new Date().toISOString());
            toast.success('Arrived at pickup location');
          } else {
            setArrivedAtDelivery(true);
            setDeliveryArrivalTime(new Date().toISOString());
            toast.success('Arrived at delivery location');
          }
        },
        () => {
          if (type === 'pickup') {
            setArrivedAtPickup(true);
            setPickupArrivalTime(new Date().toISOString());
          } else {
            setArrivedAtDelivery(true);
            setDeliveryArrivalTime(new Date().toISOString());
          }
          toast.info('Arrival confirmed manually');
        }
      );
    } else {
      if (type === 'pickup') {
        setArrivedAtPickup(true);
        setPickupArrivalTime(new Date().toISOString());
      } else {
        setArrivedAtDelivery(true);
        setDeliveryArrivalTime(new Date().toISOString());
      }
    }
  };

  // Start transport
  const startTransport = () => {
    setTransportStartTime(new Date().toISOString());
    toast.success('Package secured - Transport started');
  };

  // Complete workflow
  const handleComplete = async () => {
    setLoading(true);
    try {
      // Prepare completion data for the new job-completion API
      const completionData = {
        job_id: booking.id,
        service_type: 'courier',
        photos: [
          ...pickupPhotos.map(p => ({
            photo_data: p.data || p.url,
            photo_type: 'before',
            caption: 'Package pickup photo'
          })),
          ...deliveryPhotos.map(p => ({
            photo_data: p.data || p.url,
            photo_type: 'proof',
            caption: 'Delivery confirmation photo'
          }))
        ],
        signature_data: signatureData,
        signer_name: recipientName,
        completion_notes: `Package: ${packageDescription || 'Standard package'}. Condition: ${packageCondition}. ${deliveryNotes || ''}`.trim(),
        customer_satisfied: true,
        delivery_confirmed: true,
        recipient_name: recipientName,
        arrival_time: pickupArrivalTime,
        start_time: transportStartTime,
        end_time: new Date().toISOString()
      };

      // Try to submit to job-completion API (photos & signature upload)
      try {
        const token = localStorage.getItem('auth_token');
        if (token && (pickupPhotos.length > 0 || deliveryPhotos.length > 0 || signatureData)) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-completion/complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(completionData)
          });
        }
      } catch (uploadErr) {
        console.warn('Photo/signature upload failed, continuing with completion:', uploadErr);
      }

      toast.success('Courier delivery completed successfully!');
      onWorkflowComplete?.(completionData);
      onClose();
    } catch (error) {
      toast.error('Failed to complete delivery');
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const goToNextStep = () => {
    const stepOrder = Object.values(COURIER_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const goToPrevStep = () => {
    const stepOrder = Object.values(COURIER_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const getStepProgress = () => {
    const stepOrder = Object.values(COURIER_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    return ((currentIndex + 1) / stepOrder.length) * 100;
  };

  // Get condition badge
  const getConditionBadge = (condition) => {
    switch (condition) {
      case 'good':
        return <Badge className="bg-green-500/20 text-green-400">Good Condition</Badge>;
      case 'minor_damage':
        return <Badge className="bg-yellow-500/20 text-yellow-400">Minor Damage</Badge>;
      case 'damaged':
        return <Badge className="bg-red-500/20 text-red-400">Damaged</Badge>;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        data-testid="courier-workflow-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[var(--card)] border border-[var(--border)] rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-4 border-b border-[var(--border)] bg-gradient-to-r from-orange-600/20 to-amber-600/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <Package className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Courier Workflow</h2>
                  <p className="text-xs text-gray-400 font-mono">{booking?.reference_number || booking?.reference}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Progress</span>
                <span>{Math.round(getStepProgress())}%</span>
              </div>
              <div className="h-2 bg-[var(--background)] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-orange-500 to-amber-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${getStepProgress()}%` }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <AnimatePresence mode="wait">
              {/* OVERVIEW STEP */}
              {currentStep === COURIER_STEPS.OVERVIEW && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white">Delivery Details</h3>
                  
                  {/* Package Info */}
                  {booking?.courier_info && (
                    <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                      <p className="text-xs text-orange-400 mb-2 flex items-center gap-1">
                        <Package className="w-3 h-3" /> PACKAGE INFO
                      </p>
                      <div className="space-y-1 text-sm">
                        {booking.courier_info.package_type && (
                          <p className="text-white">Type: <span className="text-gray-300 capitalize">{booking.courier_info.package_type}</span></p>
                        )}
                        {booking.courier_info.weight && (
                          <p className="text-white">Weight: <span className="text-gray-300">{booking.courier_info.weight} lbs</span></p>
                        )}
                        {booking.courier_info.dimensions && (
                          <p className="text-white">Size: <span className="text-gray-300">{booking.courier_info.dimensions}</span></p>
                        )}
                        {booking.courier_info.fragile && (
                          <Badge className="bg-red-500/20 text-red-400 mt-2">FRAGILE</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pickup Location */}
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                    <p className="text-xs text-green-400 mb-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> PICKUP FROM
                    </p>
                    <p className="text-white font-medium">{booking?.sender_name || booking?.customer_name || 'Sender'}</p>
                    <p className="text-white text-sm">{booking?.pickup_address || booking?.address || 'Address not specified'}</p>
                    {booking?.sender_phone && (
                      <a href={`tel:${booking.sender_phone}`} className="text-green-400 text-sm flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" /> {booking.sender_phone}
                      </a>
                    )}
                  </div>

                  {/* Delivery Location */}
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400 mb-1 flex items-center gap-1">
                      <Truck className="w-3 h-3" /> DELIVER TO
                    </p>
                    <p className="text-white font-medium">{booking?.recipient_name || 'Recipient'}</p>
                    <p className="text-white text-sm">{booking?.delivery_address || booking?.dropoff_address || 'Delivery address'}</p>
                    {booking?.recipient_phone && (
                      <a href={`tel:${booking.recipient_phone}`} className="text-red-400 text-sm flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" /> {booking.recipient_phone}
                      </a>
                    )}
                  </div>

                  {/* Special Instructions */}
                  {booking?.special_instructions && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-xs text-yellow-400 mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> SPECIAL INSTRUCTIONS
                      </p>
                      <p className="text-white text-sm">{booking.special_instructions}</p>
                    </div>
                  )}

                  {/* Urgency */}
                  {booking?.urgency_level && booking.urgency_level !== 'standard' && (
                    <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                      <p className="text-sm text-purple-400 font-medium flex items-center gap-2">
                        <Timer className="w-4 h-4" />
                        {booking.urgency_level === 'express' ? 'EXPRESS DELIVERY' : 'URGENT DELIVERY'}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* PICKUP ARRIVAL STEP */}
              {currentStep === COURIER_STEPS.PICKUP_ARRIVAL && (
                <motion.div
                  key="pickup_arrival"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-400" />
                    Pickup Location
                  </h3>

                  <div className="p-4 rounded-lg bg-[var(--background)]">
                    <p className="text-white mb-2">{booking?.pickup_address || booking?.address}</p>
                    
                    <Button
                      onClick={() => {
                        const address = booking?.pickup_address || booking?.address;
                        const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
                        window.open(url, '_blank');
                      }}
                      variant="outline"
                      className="w-full mb-3"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Open in Maps
                    </Button>
                    
                    {!arrivedAtPickup ? (
                      <Button 
                        onClick={() => handleArrival('pickup')}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        Confirm Arrival at Pickup
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 text-green-400 p-3 rounded bg-green-500/10">
                        <CheckCircle className="w-5 h-5" />
                        <span>Arrived at {new Date(pickupArrivalTime).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* VERIFY SENDER STEP */}
              {currentStep === COURIER_STEPS.VERIFY_SENDER && (
                <motion.div
                  key="verify_sender"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-400" />
                    Verify Sender
                  </h3>

                  <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-blue-300 text-sm mb-3">
                      Verify the sender's identity before accepting the package.
                    </p>
                  </div>

                  <div>
                    <Label className="text-gray-400">Sender Name (as verified)</Label>
                    <Input
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder="Enter sender's name"
                      className="mt-1 bg-[var(--background)]"
                    />
                  </div>

                  <div
                    onClick={() => setSenderVerified(!senderVerified)}
                    className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer ${
                      senderVerified ? 'bg-green-500/10 border border-green-500/30' : 'bg-[var(--background)]'
                    }`}
                  >
                    {senderVerified ? (
                      <CheckSquare className="w-5 h-5 text-green-400" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-white">I have verified the sender's identity</span>
                  </div>
                </motion.div>
              )}

              {/* INSPECT PACKAGE STEP */}
              {currentStep === COURIER_STEPS.INSPECT_PACKAGE && (
                <motion.div
                  key="inspect_package"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-400" />
                    Inspect Package
                  </h3>

                  {/* Package Condition */}
                  <div>
                    <Label className="text-gray-400 mb-2 block">Package Condition</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {['good', 'minor_damage', 'damaged'].map((condition) => (
                        <Button
                          key={condition}
                          variant="outline"
                          onClick={() => setPackageCondition(condition)}
                          className={`${
                            packageCondition === condition 
                              ? condition === 'good' ? 'bg-green-500/20 border-green-500' 
                              : condition === 'minor_damage' ? 'bg-yellow-500/20 border-yellow-500'
                              : 'bg-red-500/20 border-red-500'
                              : ''
                          }`}
                        >
                          {condition === 'good' ? 'Good' : condition === 'minor_damage' ? 'Minor Dmg' : 'Damaged'}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {packageCondition !== 'good' && (
                    <div>
                      <Label className="text-gray-400">Describe Damage</Label>
                      <Textarea
                        value={conditionNotes}
                        onChange={(e) => setConditionNotes(e.target.value)}
                        placeholder="Describe any damage to the package..."
                        className="mt-1 bg-[var(--background)]"
                        rows={2}
                      />
                    </div>
                  )}

                  {/* Fragile Toggle */}
                  <div
                    onClick={() => setFragileItem(!fragileItem)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                      fragileItem ? 'bg-red-500/10 border border-red-500/30' : 'bg-[var(--background)]'
                    }`}
                  >
                    {fragileItem ? (
                      <CheckSquare className="w-5 h-5 text-red-400" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-white">Package marked as FRAGILE</span>
                  </div>

                  {/* Optional Details */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-400">Weight (lbs)</Label>
                      <Input
                        type="number"
                        value={packageWeight}
                        onChange={(e) => setPackageWeight(e.target.value)}
                        placeholder="e.g., 5"
                        className="mt-1 bg-[var(--background)]"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400">Dimensions</Label>
                      <Input
                        value={packageDimensions}
                        onChange={(e) => setPackageDimensions(e.target.value)}
                        placeholder="e.g., 12x8x6"
                        className="mt-1 bg-[var(--background)]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-400">Package Description</Label>
                    <Textarea
                      value={packageDescription}
                      onChange={(e) => setPackageDescription(e.target.value)}
                      placeholder="Brief description of package contents..."
                      className="mt-1 bg-[var(--background)]"
                      rows={2}
                    />
                  </div>
                </motion.div>
              )}

              {/* PHOTO PICKUP STEP */}
              {currentStep === COURIER_STEPS.PHOTO_PICKUP && (
                <motion.div
                  key="photo_pickup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Camera className="w-5 h-5 text-yellow-400" />
                    Pickup Photos
                  </h3>

                  <p className="text-gray-400 text-sm">
                    Take photos of the package at pickup for documentation
                  </p>

                  <div className="grid grid-cols-3 gap-2">
                    {pickupPhotos.map(photo => (
                      <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                        <img src={photo.url} alt="Pickup" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    
                    <label className="aspect-square rounded-lg border-2 border-dashed border-[var(--border)] flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e, 'pickup')}
                        disabled={uploadingPhoto}
                      />
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <Camera className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">Add Photo</span>
                        </>
                      )}
                    </label>
                  </div>

                  <p className="text-xs text-gray-500">
                    {pickupPhotos.length} photo(s) taken at pickup
                  </p>
                </motion.div>
              )}

              {/* TRANSPORT STEP */}
              {currentStep === COURIER_STEPS.TRANSPORT && (
                <motion.div
                  key="transport"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Truck className="w-5 h-5 text-purple-400" />
                    Transport Package
                  </h3>

                  {/* Package Summary */}
                  <div className="p-4 rounded-lg bg-[var(--background)]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">Package Status</span>
                      {getConditionBadge(packageCondition)}
                    </div>
                    {fragileItem && (
                      <div className="flex items-center gap-2 text-red-400 text-sm mt-2">
                        <AlertOctagon className="w-4 h-4" />
                        <span>Handle with care - FRAGILE</span>
                      </div>
                    )}
                  </div>

                  {/* Delivery Destination */}
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400 mb-1">DELIVER TO</p>
                    <p className="text-white">{booking?.delivery_address || booking?.dropoff_address}</p>
                  </div>

                  <Button
                    onClick={() => {
                      const address = booking?.delivery_address || booking?.dropoff_address;
                      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
                      window.open(url, '_blank');
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Navigate to Delivery
                  </Button>

                  {!transportStartTime ? (
                    <Button 
                      onClick={startTransport}
                      variant="outline"
                      className="w-full"
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      Start Transport
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 text-purple-400 p-3 rounded bg-purple-500/10">
                      <Timer className="w-4 h-4" />
                      <span>En route since {new Date(transportStartTime).toLocaleTimeString()}</span>
                    </div>
                  )}
                </motion.div>
              )}

              {/* DELIVERY ARRIVAL STEP */}
              {currentStep === COURIER_STEPS.DELIVERY_ARRIVAL && (
                <motion.div
                  key="delivery_arrival"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-400" />
                    Delivery Location
                  </h3>

                  <div className="p-4 rounded-lg bg-[var(--background)]">
                    <p className="text-white mb-4">{booking?.delivery_address || booking?.dropoff_address}</p>
                    
                    {!arrivedAtDelivery ? (
                      <Button 
                        onClick={() => handleArrival('delivery')}
                        className="w-full bg-red-600 hover:bg-red-700"
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        Confirm Arrival at Delivery
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 text-red-400 p-3 rounded bg-red-500/10">
                        <CheckCircle className="w-5 h-5" />
                        <span>Arrived at {new Date(deliveryArrivalTime).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* VERIFY RECIPIENT STEP */}
              {currentStep === COURIER_STEPS.VERIFY_RECIPIENT && (
                <motion.div
                  key="verify_recipient"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-400" />
                    Verify Recipient
                  </h3>

                  <div>
                    <Label className="text-gray-400">Recipient Name (as verified)</Label>
                    <Input
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Enter recipient's name"
                      className="mt-1 bg-[var(--background)]"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400 mb-2 block">Delivery Location</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'door', label: 'Front Door' },
                        { id: 'reception', label: 'Reception' },
                        { id: 'mailroom', label: 'Mailroom' },
                        { id: 'other', label: 'Other' }
                      ].map((loc) => (
                        <Button
                          key={loc.id}
                          variant="outline"
                          onClick={() => setDeliveryLocation(loc.id)}
                          className={deliveryLocation === loc.id ? 'bg-blue-500/20 border-blue-500' : ''}
                        >
                          {loc.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div
                    onClick={() => setRecipientVerified(!recipientVerified)}
                    className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer ${
                      recipientVerified ? 'bg-green-500/10 border border-green-500/30' : 'bg-[var(--background)]'
                    }`}
                  >
                    {recipientVerified ? (
                      <CheckSquare className="w-5 h-5 text-green-400" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="text-white">I have verified the recipient's identity</span>
                  </div>

                  <div>
                    <Label className="text-gray-400">Delivery Notes</Label>
                    <Textarea
                      value={deliveryNotes}
                      onChange={(e) => setDeliveryNotes(e.target.value)}
                      placeholder="Any notes about the delivery..."
                      className="mt-1 bg-[var(--background)]"
                      rows={2}
                    />
                  </div>
                </motion.div>
              )}

              {/* PHOTO DELIVERY STEP */}
              {currentStep === COURIER_STEPS.PHOTO_DELIVERY && (
                <motion.div
                  key="photo_delivery"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Image className="w-5 h-5 text-green-400" />
                    Delivery Confirmation Photos
                  </h3>

                  <p className="text-gray-400 text-sm">
                    Take photos confirming successful delivery
                  </p>

                  <div className="grid grid-cols-3 gap-2">
                    {deliveryPhotos.map(photo => (
                      <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                        <img src={photo.url} alt="Delivery" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    
                    <label className="aspect-square rounded-lg border-2 border-dashed border-[var(--border)] flex flex-col items-center justify-center cursor-pointer hover:border-green-500 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e, 'delivery')}
                        disabled={uploadingPhoto}
                      />
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <Camera className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">Add Photo</span>
                        </>
                      )}
                    </label>
                  </div>

                  <p className="text-xs text-gray-500">
                    {deliveryPhotos.length} photo(s) taken at delivery
                  </p>
                </motion.div>
              )}

              {/* SIGNATURE STEP */}
              {currentStep === COURIER_STEPS.SIGNATURE && (
                <motion.div
                  key="signature"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Pen className="w-5 h-5 text-yellow-400" />
                    Recipient Signature
                  </h3>

                  <div className="p-3 rounded-lg bg-[var(--background)]">
                    <p className="text-sm text-gray-300 mb-1">Recipient:</p>
                    <p className="text-white font-medium">{recipientName || booking?.recipient_name || 'Recipient'}</p>
                  </div>

                  <div>
                    <Label className="text-gray-400">Signature</Label>
                    <div className="mt-1 border-2 border-dashed border-[var(--border)] rounded-lg overflow-hidden bg-white">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        className="w-full touch-none cursor-crosshair"
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={clearSignature} className="mt-2">
                      Clear
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* COMPLETE STEP */}
              {currentStep === COURIER_STEPS.COMPLETE && (
                <motion.div
                  key="complete"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Ready to Complete</h3>
                    <p className="text-gray-400 mt-2">Review delivery summary</p>
                  </div>

                  <div className="space-y-2 p-4 rounded-lg bg-[var(--background)]">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Sender Verified</span>
                      <span className={senderVerified ? 'text-green-400' : 'text-yellow-400'}>
                        {senderVerified ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Package Condition</span>
                      <span className="text-white capitalize">{packageCondition.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Pickup Photos</span>
                      <span className="text-white">{pickupPhotos.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Delivery Photos</span>
                      <span className="text-white">{deliveryPhotos.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Recipient Verified</span>
                      <span className={recipientVerified ? 'text-green-400' : 'text-yellow-400'}>
                        {recipientVerified ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Signature</span>
                      <span className={signatureData ? 'text-green-400' : 'text-yellow-400'}>
                        {signatureData ? 'Collected' : 'Pending'}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleComplete}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Complete Delivery
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-[var(--border)] flex justify-between">
            <Button
              variant="outline"
              onClick={goToPrevStep}
              disabled={currentStep === COURIER_STEPS.OVERVIEW}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            {currentStep !== COURIER_STEPS.COMPLETE && (
              <Button onClick={goToNextStep}>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CourierWorkflowModal;
